import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import './AiCompanion.css';

function AiCompanion({ channelSlug }) {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: "Hey 💜 I'm here if you want to talk. Whether you're processing something, feeling alone in your experience, or just curious about this community — I'm listening. What's on your mind?"
    }
  ]);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [rateLimited, setRateLimited] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Remember minimized state across navigations
  useEffect(() => {
    const wasOpen = localStorage.getItem('belong_companion_open');
    if (wasOpen === 'true') setIsOpen(true);
  }, []);

  useEffect(() => {
    localStorage.setItem('belong_companion_open', isOpen ? 'true' : 'false');
  }, [isOpen]);

  // Scroll to bottom when new messages arrive
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, isTyping]);

  // Focus input when opened
  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 300);
    }
  }, [isOpen]);

  const sendMessage = async () => {
    const trimmed = input.trim();
    if (!trimmed || isTyping || rateLimited) return;

    const userMessage = { role: 'user', content: trimmed };
    const updatedMessages = [...messages, userMessage];
    setMessages(updatedMessages);
    setInput('');
    setIsTyping(true);

    try {
      // Build history (last 5 exchanges for context)
      const history = updatedMessages
        .filter(m => m.role !== 'system')
        .slice(-10)
        .map(m => ({ role: m.role, content: m.content }));

      const res = await axios.post('/api/public/ai-companion/chat', {
        message: trimmed,
        context: channelSlug || null,
        history: history.slice(0, -1) // exclude the current message from history
      });

      setMessages(prev => [...prev, { role: 'assistant', content: res.data.reply }]);
    } catch (err) {
      if (err.response?.status === 429) {
        setRateLimited(true);
        const fallbackReply = err.response?.data?.reply ||
          "You've been chatting a lot — I love that you're reaching out. I've hit my limit for now, but there are real people in this community who'd love to connect. Join Belong to keep the conversation going. 💜";
        setMessages(prev => [...prev, { role: 'assistant', content: fallbackReply }]);
      } else {
        const errorReply = err.response?.data?.reply ||
          "I'm having a moment — sorry about that. Feel free to try again, or browse the community discussions. 💜";
        setMessages(prev => [...prev, { role: 'assistant', content: errorReply }]);
      }
    } finally {
      setIsTyping(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const shouldShowJoinButton = (content) => {
    if (!content) return false;
    const lower = content.toLowerCase();
    return lower.includes('join belong') || lower.includes('connect with') || lower.includes('real people');
  };

  return (
    <>
      {/* Floating bubble */}
      {!isOpen && (
        <button
          className="ai-companion-bubble"
          onClick={() => setIsOpen(true)}
          aria-label="Open AI companion chat"
        >
          <span className="ai-companion-bubble-icon">💬</span>
          <span className="ai-companion-bubble-text">Need to talk?</span>
        </button>
      )}

      {/* Chat panel */}
      {isOpen && (
        <div className="ai-companion-panel" role="dialog" aria-label="AI Companion Chat">
          {/* Header */}
          <div className="ai-companion-header">
            <div className="ai-companion-header-info">
              <span className="ai-companion-header-avatar">💜</span>
              <div>
                <div className="ai-companion-header-name">Belong Companion</div>
                <div className="ai-companion-header-status">Here to listen</div>
              </div>
            </div>
            <button
              className="ai-companion-close"
              onClick={() => setIsOpen(false)}
              aria-label="Close chat"
            >
              ✕
            </button>
          </div>

          {/* Messages */}
          <div className="ai-companion-messages">
            {messages.map((msg, i) => (
              <div key={i} className={`ai-companion-msg ai-companion-msg-${msg.role}`}>
                <div className="ai-companion-msg-bubble">
                  {msg.content}
                </div>
                {msg.role === 'assistant' && shouldShowJoinButton(msg.content) && (
                  <Link to="/register" className="ai-companion-join-btn">
                    Join Belong →
                  </Link>
                )}
              </div>
            ))}
            {isTyping && (
              <div className="ai-companion-msg ai-companion-msg-assistant">
                <div className="ai-companion-msg-bubble ai-companion-typing">
                  <span className="ai-companion-dot"></span>
                  <span className="ai-companion-dot"></span>
                  <span className="ai-companion-dot"></span>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="ai-companion-input-area">
            {rateLimited ? (
              <div className="ai-companion-rate-limit">
                <span>You've reached the free message limit.</span>
                <Link to="/register" className="ai-companion-rate-limit-link">
                  Join Belong for free →
                </Link>
              </div>
            ) : (
              <form onSubmit={(e) => { e.preventDefault(); sendMessage(); }} className="ai-companion-form">
                <input
                  ref={inputRef}
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Type a message..."
                  className="ai-companion-input"
                  maxLength={1000}
                  disabled={isTyping}
                  aria-label="Chat message"
                />
                <button
                  type="submit"
                  className="ai-companion-send"
                  disabled={!input.trim() || isTyping}
                  aria-label="Send message"
                >
                  ↑
                </button>
              </form>
            )}
            <div className="ai-companion-disclaimer">
              Anonymous · No messages stored · Not a therapist
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default AiCompanion;
